import { buildSortParams, type SortDirection, type SortState } from "@investhinks/api-client";
import { useCallback, useState } from "react";

export type { SortDirection };
export type StrapiSort = SortState;

export interface UseStrapiSortReturn {
  sort: StrapiSort;
  setSort: React.Dispatch<React.SetStateAction<StrapiSort>>;
  changeSort: (field: string | StrapiSort, direction?: SortDirection) => void;
  clearSort: () => void;
  sortParams: string[] | undefined;
}

/**
 * Hook for managing Strapi sort state
 * Accepts flexible sort input (Record<string, string>) and normalizes to StrapiSort
 */
const useStrapiSort = (initialSort: StrapiSort = {}): UseStrapiSortReturn => {
  const [sort, setSortState] = useState<StrapiSort>(initialSort);

  const changeSort = useCallback((field: string | StrapiSort, direction?: SortDirection) => {
    if (typeof field === "object") {
      // Passed entire sort object
      setSortState(field);
    } else if (direction) {
      // Passed field and direction
      setSortState({ [field]: direction });
    } else {
      // Toggle or set single field
      setSortState((prev) => {
        const currentDirection = prev[field];
        if (!currentDirection) return { [field]: "desc" };
        if (currentDirection === "desc") return { [field]: "asc" };
        return {}; // Clear sort
      });
    }
  }, []);

  const clearSort = useCallback(() => {
    setSortState({});
  }, []);

  // Convert { field: "up" | "down" } to ["field:asc"] format for API
  const sortParams = buildSortParams(sort);

  return {
    sort,
    setSort: setSortState,
    changeSort,
    clearSort,
    sortParams,
  };
};

export default useStrapiSort;
